import React, { useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import Button from '../../../../shared/design-system/components/atoms/button';
import LinkBadge from '../../../../shared/design-system/components/atoms/link-badge';
import toaster from '../../../../shared/toaster';
import { RequestStatus } from '../../../../shared/enums/request-status';
import { executeOnRequestStatus } from '../../../../shared/utils';
import type { IProps } from './mcp-settings-container';
import { McpSession } from './types';
import McpInfoBanner from './components/mcp-info-banner/mcp-info-banner';
import McpSessionsTable from './components/mcp-sessions-table/mcp-sessions-table';
import McpSetupModal from './components/mcp-setup-modal/mcp-setup-modal';
import McpDisconnectModal from './components/mcp-disconnect-modal/mcp-disconnect-modal';

const MCP_DOCS_URL =
  'https://docs.saleshandy.com/en/articles/13367892-saleshandy-mcp-model-context-protocol';

const McpSettings: React.FC<IProps> = ({
  sessions,
  getMcpSessionsRequestStatus,
  revokeMcpSessionRequestStatus,
  sendGetMcpSessionsRequest,
  sendRevokeMcpSessionRequest,
  resetRevokeMcpSessionRequest,
  generatedApiToken,
  createMcpApiKeyRequestStatus,
  mcpApiKeyExists,
  sendCreateMcpApiKeyRequest,
  sendFetchMcpApiKeyRequest,
  resetCreateMcpApiKeyRequest,
  showLoading,
  hideLoading,
}) => {
  const [showInfoBanner, setShowInfoBanner] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<McpSession | null>(null);

  const isRevokeLoading =
    revokeMcpSessionRequestStatus === RequestStatus.Pending;

  // Fetch sessions on mount
  useEffect(() => {
    sendGetMcpSessionsRequest();
  }, []);

  // Loading bar
  useEffect(() => {
    executeOnRequestStatus({
      status: getMcpSessionsRequestStatus,
      onPending: () => showLoading(),
      onSuccess: () => hideLoading(),
    });
  }, [getMcpSessionsRequestStatus]);

  // Handle revoke result
  useEffect(() => {
    if (revokeMcpSessionRequestStatus === RequestStatus.Succeeded) {
      sendGetMcpSessionsRequest();
      setRevokeTarget(null);
      toaster.success('MCP session disconnected successfully.');
      resetRevokeMcpSessionRequest();
    }
    if (revokeMcpSessionRequestStatus === RequestStatus.Failed) {
      setRevokeTarget(null);
      resetRevokeMcpSessionRequest();
    }
  }, [revokeMcpSessionRequestStatus]);

  const handleDisconnect = () => {
    if (revokeTarget) {
      sendRevokeMcpSessionRequest({ sessionId: revokeTarget.id });
    }
  };

  return (
    <div className="mcp-settings-container">
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <div className="mcp-settings-header">
            <div className="mcp-settings-header__left">
              <h1 className="mcp-settings-header__title">
                MCP (AI Assistants)
              </h1>
              <span className="mcp-settings-header__badge">NEW</span>
              <LinkBadge label="View MCP Docs" url={MCP_DOCS_URL} />
            </div>
            <Button
              variant={Button.Variant.Primary}
              onClick={() => setShowSetupModal(true)}
            >
              Set Up MCP
            </Button>
          </div>
        </Col>
      </Row>

      {/* Info Banner */}
      {showInfoBanner && (
        <McpInfoBanner onDismiss={() => setShowInfoBanner(false)} />
      )}

      {/* Sessions Table */}
      <div className="mcp-settings-table-list">
        {getMcpSessionsRequestStatus === RequestStatus.Succeeded && (
          <McpSessionsTable
            sessions={sessions}
            onDisconnect={(session) => setRevokeTarget(session)}
          />
        )}
      </div>

      {/* Setup Modal */}
      <McpSetupModal
        show={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        generatedApiToken={generatedApiToken}
        createApiKeyRequestStatus={createMcpApiKeyRequestStatus}
        mcpApiKeyExists={mcpApiKeyExists}
        sendCreateMcpApiKeyRequest={sendCreateMcpApiKeyRequest}
        sendFetchMcpApiKeyRequest={sendFetchMcpApiKeyRequest}
        resetCreateMcpApiKeyRequest={resetCreateMcpApiKeyRequest}
      />

      {/* Disconnect Modal */}
      <McpDisconnectModal
        show={!!revokeTarget}
        session={revokeTarget}
        isLoading={isRevokeLoading}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleDisconnect}
      />
    </div>
  );
};

export default McpSettings;
